import type { ComponentProps } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Tooltip from "@/v2-ui/tooltip"
import {
  getCoverageMonthTicks,
  issueGapBar,
  parseCoverageDate,
  toCoverageBar,
  toWindowPercent,
  type LicenseCoverageIssueRow,
  type LicenseCoverageSegmentRow,
  type LicenseCoverageWindow,
} from "@/v2-backoffice-client/license/coverage/license.coverage.utils"

export type LicenseCoverageTimelineProps = ComponentProps<"div"> & {
  segments: LicenseCoverageSegmentRow[]
  issues: LicenseCoverageIssueRow[]
  window: LicenseCoverageWindow
  /** Draw the month axis under the lanes. */
  showAxis?: boolean
  /** Narrow host (the customer panel): label quarters, not every month. */
  compact?: boolean
}

// Bar classes by what the bar is: green lets people in, amber is the
// operator's time-boxed word, grey is history or a promise, red is money
// owed. A scheduled bar is outlined — a promise, not access.
const SUBSCRIPTION_CLASSES: Record<string, string> = {
  active: "bg-green-500/80",
  not_renewing: "bg-green-500/50",
  scheduled:
    "border border-dashed border-muted-foreground/70 bg-muted-foreground/10",
  expired: "bg-muted-foreground/25",
  not_starting:
    "border border-dotted border-muted-foreground/50 bg-muted-foreground/15",
}
const OPERATOR_CLASSES: Record<string, string> = {
  trial: "bg-amber-500/70",
  active: "bg-green-500/55",
  expired: "bg-amber-500/25",
  other: "bg-muted-foreground/25",
}
const INVOICE_CLASSES: Record<string, string> = {
  paid: "bg-foreground/35",
  open: "bg-amber-500/60",
  due: "bg-amber-500/80",
  nonpayment: "bg-red-500/70",
}

const LANES = [
  { kind: "subscription", label: "Billing" },
  { kind: "operator", label: "Operator" },
  { kind: "invoice", label: "Invoiced" },
] as const

// One license's year back and year ahead as three lanes — the Fenerum
// subscriptions (what automates access), the operator's own row (the
// manual override), the invoice lines (what the customer paid for) — with
// today as a line and a billing gap shaded red across all three. The
// shapes make the Atlas slip legible at a glance: a grey bar ending, a red
// hole, a dashed promise far to the right, and an invoice bar running
// straight through the hole.
function LicenseCoverageTimeline(props: LicenseCoverageTimelineProps) {
  const {
    segments,
    issues,
    window,
    showAxis,
    compact,
    className,
    ...divProps
  } = props

  const todayPct = toWindowPercent(window.today, window)
  const gaps = issues
    .map((issue) => ({ issue, bar: issueGapBar(issue, window) }))
    .filter((gap) => gap.bar !== null)
  const ticks = getCoverageMonthTicks(window)

  return (
    <div
      className={cn("license-coverage-timeline flex gap-2", className)}
      {...divProps}
    >
      <div className="flex w-14 shrink-0 flex-col gap-1 py-1">
        {LANES.map((lane) => (
          <span
            key={lane.kind}
            className="h-3 text-[10px] uppercase leading-3 tracking-wide text-muted-foreground"
          >
            {lane.label}
          </span>
        ))}
        {showAxis && <span className="h-4" />}
      </div>

      <div className="min-w-0 flex-1">
        <div className="relative">
          {/* Month grid behind the lanes. */}
          <div className="pointer-events-none absolute inset-0">
            {ticks.map((tick) => (
              <span
                key={`grid-${tick.pct}`}
                className={cn(
                  "absolute inset-y-0 border-l",
                  tick.isYearStart ? "border-border" : "border-border/40",
                )}
                style={{ left: `${tick.pct}%` }}
              />
            ))}
          </div>

          <div className="flex flex-col gap-1 py-1">
            {LANES.map((lane) => (
              <div key={lane.kind} className="relative h-3">
                {segments
                  .filter((segment) => segment.Kind === lane.kind)
                  .map((segment, index) => (
                    <SegmentBar
                      key={`${segment.Kind}-${segment.FromDate ?? ""}-${index}`}
                      segment={segment}
                      window={window}
                    />
                  ))}
              </div>
            ))}
          </div>

          {/* The hole(s), across every lane. */}
          {gaps.map(({ issue, bar }) =>
            bar ? (
              <Tooltip key={`${issue.Code}-${issue.FromDate ?? ""}`}>
                <Tooltip.Trigger asChild>
                  <span
                    className="absolute inset-y-0 border-x border-red-500/70 bg-red-500/15"
                    style={{
                      left: `${bar.leftPct}%`,
                      width: `${bar.widthPct}%`,
                    }}
                    aria-label={issue.Message}
                  />
                </Tooltip.Trigger>
                <Tooltip.Content
                  side="top"
                  className="ui-tooltip-content--panel max-w-[320px]"
                >
                  {issue.Message}
                </Tooltip.Content>
              </Tooltip>
            ) : null,
          )}

          {/* Today. */}
          <span
            className="pointer-events-none absolute inset-y-0 border-l-2 border-foreground/80"
            style={{ left: `${todayPct}%` }}
            aria-hidden
          />
        </div>

        {showAxis && (
          <div className="relative h-4 text-[10px] text-muted-foreground">
            {ticks
              .filter((tick) => !compact || tick.isQuarterStart)
              .map((tick) => (
                <span
                  key={`axis-${tick.pct}`}
                  className={cn(
                    "absolute -translate-x-1/2",
                    tick.isYearStart && "font-medium text-foreground",
                  )}
                  style={{ left: `${tick.pct}%` }}
                >
                  {tick.label}
                </span>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}

type SegmentBarProps = {
  segment: LicenseCoverageSegmentRow
  window: LicenseCoverageWindow
}

function SegmentBar(props: SegmentBarProps) {
  const { segment, window } = props
  const from = parseCoverageDate(segment.FromDate)
  const to = parseCoverageDate(segment.ToDate)
  const bar = toCoverageBar(from, to, window)
  if (!bar) return null

  const classes =
    segment.Kind === "subscription"
      ? (SUBSCRIPTION_CLASSES[segment.Status] ?? "bg-muted-foreground/25")
      : segment.Kind === "operator"
        ? (OPERATOR_CLASSES[segment.Status] ?? "bg-muted-foreground/25")
        : (INVOICE_CLASSES[segment.Status] ?? "bg-foreground/25")

  const period = `${from ? formatDateString(from) : "…"} – ${to ? formatDateString(to) : "open"}`
  const amount =
    segment.Amount != null
      ? ` · ${formatNumber(Math.round(segment.Amount))}${segment.Currency ? ` ${segment.Currency}` : ""}`
      : ""

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <span
          className={cn(
            "absolute inset-y-0 rounded-sm",
            classes,
            bar.openEnded && "rounded-r-none",
            bar.clippedStart && "rounded-l-none",
            segment.Kind === "invoice" &&
              !segment.Matched &&
              "ring-1 ring-red-500/70",
          )}
          style={{ left: `${bar.leftPct}%`, width: `${bar.widthPct}%` }}
          aria-label={`${segment.Label}: ${segment.Status}, ${period}`}
        />
      </Tooltip.Trigger>
      <Tooltip.Content
        side="top"
        className="ui-tooltip-content--panel max-w-[320px]"
      >
        <span className="font-medium">{segment.Label}</span>
        <br />
        {segment.Status} · {period}
        {amount}
        {segment.Kind === "invoice" && !segment.Matched && (
          <>
            <br />
            On no subscription: attributed to this license because nothing
            else is live.
          </>
        )}
      </Tooltip.Content>
    </Tooltip>
  )
}

export default LicenseCoverageTimeline
